/**
 * KAN-882 — per-USER "already greeted" receipts for the first-teammate-joined celebration. Same shape as
 * `../billing/subscribeCelebrationReceipts.js`: a per-user, per-tenant local map + write-through via
 * `upsertNoticeReceipts` + MONOTONIC merge from the server through `NoticeReceiptsSync`.
 *
 * notice_key = `teammate_joined:<first_teammate_joins.id>` — the row id only (never a name or an email).
 * Written when the celebration is SHOWN, so a reload, a second tab or a second device never replays it.
 */
import { upsertNoticeReceipts } from '../../services/noticeReceipts';
import { isNewerIso, maxIso } from '../notices/noticeReceiptMath';
import { tenantIdForNoticeWrite } from '../notices/noticeReceiptsSession';

export const TEAMMATE_JOINED_PREFIX = 'teammate_joined:';
export const TEAMMATE_JOINED_GREETED_PREFIX = 'cvx-teammate-joined-greeted-v1:';
const MAX_ENTRIES = 10;

export const teammateJoinedNoticeKey = (rowId) => (rowId ? `${TEAMMATE_JOINED_PREFIX}${rowId}` : null);

export function ownsTeammateJoinedNoticeKey(noticeKey) {
  return typeof noticeKey === 'string' && noticeKey.startsWith(TEAMMATE_JOINED_PREFIX);
}

export const teammateJoinedGreetedKey = (userId, tenantId) => `${TEAMMATE_JOINED_GREETED_PREFIX}${tenantId || 'tenant'}:${userId || 'anon'}`;

const listeners = new Set();
export function subscribeTeammateJoinedReceiptChanges(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
function notify() {
  listeners.forEach((fn) => { try { fn(); } catch (_e) { /* never break the others */ } });
}

function capNewest(map) {
  const keep = Object.keys(map).sort((a, b) => Date.parse(map[b]) - Date.parse(map[a])).slice(0, MAX_ENTRIES);
  const out = {};
  keep.forEach((k) => { out[k] = map[k]; });
  return out;
}

/** `{ noticeKey: isoInstant }`. No user id -> nothing. Unreadable -> empty. */
export function readTeammateJoinedGreeted(userId, tenantId, storage) {
  if (!userId) return {};
  try {
    const raw = (storage || window.localStorage).getItem(teammateJoinedGreetedKey(userId, tenantId));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out = {};
    Object.keys(parsed).forEach((k) => {
      if (!ownsTeammateJoinedNoticeKey(k) || Number.isNaN(Date.parse(parsed[k]))) return;
      out[k] = new Date(Date.parse(parsed[k])).toISOString();
    });
    return out;
  } catch {
    return {};
  }
}

export function hasGreetedTeammateJoined(userId, tenantId, noticeKey, storage) {
  return Boolean(noticeKey) && Boolean(readTeammateJoinedGreeted(userId, tenantId, storage)[noticeKey]);
}

function writeMap(userId, tenantId, next, storage) {
  try {
    (storage || window.localStorage).setItem(teammateJoinedGreetedKey(userId, tenantId), JSON.stringify(capNewest(next)));
  } catch {
    /* best-effort local cache */
  }
}

/** Marks the celebration greeted (max, never `=`) and writes through to the server. */
export function markTeammateJoinedGreeted(userId, tenantId, noticeKey, storage, nowMs = Date.now(), opts = {}) {
  if (!userId || !tenantId || !ownsTeammateJoinedNoticeKey(noticeKey)) return;
  const current = readTeammateJoinedGreeted(userId, tenantId, storage);
  const stamp = maxIso(current[noticeKey], new Date(nowMs).toISOString());
  writeMap(userId, tenantId, { ...current, [noticeKey]: stamp }, storage);
  if (!opts.skipSync && tenantIdForNoticeWrite(userId) === tenantId) {
    upsertNoticeReceipts({ tenantId, userId, rows: [{ notice_key: noticeKey, announced_at: stamp }] });
  }
  notify();
}

/** Server rows -> local map, MONOTONIC. */
export function mergeTeammateJoinedFromServer(userId, tenantId, rows, storage) {
  if (!userId || !Array.isArray(rows)) return;
  let merged = readTeammateJoinedGreeted(userId, tenantId, storage);
  let changed = false;
  rows.forEach((row) => {
    if (!ownsTeammateJoinedNoticeKey(row?.notice_key) || !row.announced_at) return;
    if (!isNewerIso(row.announced_at, merged[row.notice_key])) return;
    merged = { ...merged, [row.notice_key]: row.announced_at };
    changed = true;
  });
  if (!changed) return;
  writeMap(userId, tenantId, merged, storage);
  notify();
}

/** Snapshot for the one-time post-hydration upload. */
export function collectTeammateJoinedForUpload(userId, tenantId, storage) {
  const map = readTeammateJoinedGreeted(userId, tenantId, storage);
  return Object.keys(map).map((k) => ({ notice_key: k, announced_at: map[k] }));
}
